// "Who is affected?" — a chat card for a spell the caster owns becomes a
// dialog over the tracker's creatures: damage (Full / Half / Saved), a
// condition (Failed / Saved), healing, or a timed buff. One Apply writes it
// all: party members through their sheets, tokens through their bars and
// tooltips, with one chat line per creature.
import type { Character, Condition } from "./types";
import { spellInfo, casterOf, type SpellInfo } from "./spellRules";
import { applyHit } from "./ddbLive";
import { normName as norm } from "./text";

export type AffectMode = "damage" | "heal" | "condition" | "buff";
export interface AffectTarget { key: string; name: string; kind: "pc" | "npc"; characterId?: string; tokenId?: string; entryId: string; checked: boolean; share: "full" | "half" | "none"; failed: boolean }
export interface Affect { cardId: string; spell: SpellInfo; casterId: string; casterName: string; mode: AffectMode; amount: number; conditionName: string; rounds: number | null; concentration: boolean; targets: AffectTarget[] }

const CONDITION_NAMES = new Set(["Blinded", "Charmed", "Deafened", "Frightened", "Grappled", "Incapacitated", "Invisible", "Paralyzed", "Petrified", "Poisoned", "Prone", "Restrained", "Stunned", "Unconscious"]);

/** "Fireball (Edam)" → { spell: "Fireball", caster: "Edam" }. */
export function splitCaption(caption: string): { spell: string; caster: string } {
  const m = String(caption || "").match(/^(.*?)\s*\(([^()]+)\)\s*$/);
  return m ? { spell: m[1].trim(), caster: m[2].trim() } : { spell: String(caption || "").trim(), caster: "" };
}

export type AffectDecision = { ok: true; spell: SpellInfo; caster: Character; mode: AffectMode } | { ok: false; reason: "no-spell" | "not-owned" | "no-handler"; spell?: SpellInfo; casterName: string };

/** Can this card open the dialog? The gate: the named caster must own the spell —
 *  OR the card is a weapon ATTACK by a party member (a damage roll with a roster
 *  caster), which opens damage mode so the DM picks who was hit. */
export function decideAffect(roster: Character[], caption: string, rows: { label: string; total: number | null }[]): AffectDecision {
  const { spell: name, caster: casterName } = splitCaption(caption);
  const info = spellInfo(name);
  if (!info) {
    // Not an inventory spell. A party member's WEAPON ATTACK is still worth the picker:
    // a card with a damage roll and a caster on the roster opens damage mode so the DM
    // picks who was hit and applies it (the same flow a spell uses). Attacks carry NO
    // range anywhere in the DMS, so the synthetic definition leaves range empty — the
    // picker then sorts targets by distance from the attacker and shows everyone with no
    // out-of-range mark (see openAffect's isAttack path). Weapon attacks only: a card
    // with no damage roll, or by someone not on the roster (e.g. a creature), is left be.
    const who = norm(casterName);
    const attacker = who ? (roster.find((c) => norm(c.name) === who) || roster.find((c) => norm(c.name).split(" ")[0] === who.split(" ")[0])) : undefined;
    const hasDamageRoll = rows.some((r) => /damage|dmg/i.test(r.label) && r.total !== null);
    if (attacker && hasDamageRoll) {
      const attack: SpellInfo = { name, level: "", handler: "attack", duration: "", rounds: null, concentration: false, range: "", save: "", effect: "" };
      return { ok: true, spell: attack, caster: attacker, mode: "damage" };
    }
    return { ok: false, reason: "no-spell", casterName };
  }
  const caster = casterOf(roster, casterName, name);
  if (!caster) return { ok: false, reason: "not-owned", spell: info, casterName };
  const h = info.handler;
  const hasDamage = rows.some((r) => /damage|dmg/i.test(r.label) && r.total !== null);
  const hasHeal = rows.some((r) => /heal/i.test(r.label) && r.total !== null);
  let mode: AffectMode | null = null;
  if (h === "healing" || hasHeal) mode = "heal";
  else if (h === "burst-save" || h === "burst" || h === "target-save") mode = hasDamage ? "damage" : (info.effect && CONDITION_NAMES.has(info.effect) ? "condition" : "damage");
  else if (h === "target-condition") mode = "condition";
  else if (h === "target-save-timed") mode = "condition";
  else if (h === "buff-timed") mode = "buff";
  /* A zone that ROLLS damage is damage; one that imposes a CONDITION and rolls none
     (Sleep → Incapacitated, Sleet Storm → Prone) is a condition — without this it fell
     through to no-handler and the "Choose who is hit" card silently refused it, so a cast
     Sleep applied nothing (reported from a live table). A zone that is neither — Darkness,
     Fog Cloud, Silence (effect "Control", no save) — stays no-handler: there is nothing to
     apply, and opening a dialog for it would be the noise burst-save's fallback avoids. */
  else if (h === "zone") mode = hasDamage ? "damage" : (info.effect && CONDITION_NAMES.has(info.effect) ? "condition" : null);
  if (!mode) return { ok: false, reason: "no-handler", spell: info, casterName };
  return { ok: true, spell: info, caster, mode };
}

export function buildAffect(decision: Extract<AffectDecision, { ok: true }>, cardId: string, rows: { label: string; total: number | null }[], entries: { id: string; name: string; kind: string; tokenId?: string }[], insideKeys: Set<string>, allyKey: (e: { id: string; tokenId?: string; name: string }) => string): Affect {
  const { spell, caster, mode } = decision;
  const total = (re: RegExp) => rows.find((r) => re.test(r.label) && r.total !== null)?.total ?? 0;
  const amount = mode === "heal" ? total(/heal/i) || total(/damage|dmg/i) : mode === "damage" ? total(/damage|dmg/i) : 0;
  const conditionName = mode === "condition" ? (spell.effect && CONDITION_NAMES.has(spell.effect) ? spell.effect : spell.name) : mode === "buff" ? spell.name : "";
  const targets: AffectTarget[] = entries.map((e) => {
    const key = e.kind === "pc" ? `c:${e.id}` : allyKey(e);
    return { key, name: e.name, kind: e.kind === "pc" ? "pc" : "npc", characterId: e.kind === "pc" ? e.id : undefined, tokenId: e.kind === "pc" ? undefined : (e.tokenId && e.tokenId !== "-1" ? e.tokenId : undefined), entryId: e.id, checked: insideKeys.has(key), share: "full", failed: true };
  });
  return { cardId, spell, casterId: caster.id, casterName: caster.name, mode, amount, conditionName, rounds: spell.rounds, concentration: spell.concentration, targets };
}

/** The picker lists targets NEAREST-FIRST from the point of origin, so the DM is not
 *  sifting an unsorted crowd of forty tokens. `reach[key].ft` is the footprint distance
 *  (the target's nearest occupied square-centre, so a Large creature with one square in
 *  range reads as in range); a target with no reach entry — off the map, or unknown —
 *  sorts last. Pure, so the picker's order is tested without a DOM. */
export function sortByReach<T extends { key: string }>(targets: T[], reach: Record<string, { ft: number } | undefined>): T[] {
  const ft = (k: string) => { const r = reach[k]; return r ? r.ft : Number.POSITIVE_INFINITY; };
  return [...targets].sort((a, b) => ft(a.key) - ft(b.key));
}

/** The changes for a party member. */
export function affectCharacter(c: Character, a: Affect, t: AffectTarget): Partial<Character> | null {
  if (a.mode === "damage") { const dmg = t.share === "full" ? a.amount : t.share === "half" ? Math.floor(a.amount / 2) : 0; return dmg > 0 ? applyHit(c, dmg, false) : null; }
  if (a.mode === "heal") { const hp = Math.min(Number(c.maxHp || 0), Number(c.hp || 0) + a.amount); return hp !== Number(c.hp || 0) ? { hp, ...(Number(c.hp || 0) <= 0 ? { deathSuccesses: 0, deathFailures: 0 } : {}) } : null; }
  if (a.mode === "condition" && !t.failed) return null;
  const cond = spellCondition(a);
  const names = (c.conditions || []).map((x) => norm(typeof x === "string" ? x : String(x.name || x.condition || "")));
  if (names.includes(norm(a.conditionName))) return null;
  return { conditions: [...(c.conditions || []), cond] };
}

/** The condition/effect chip a spell leaves, anchored to the caster's turn and tagged with its source. */
export function spellCondition(a: Affect): string | Condition {
  if (!a.rounds && !a.concentration) return a.conditionName;
  return { name: a.conditionName, remaining: a.rounds ?? undefined, total: a.rounds ?? undefined, phase: "end", anchorId: a.casterId, anchorName: a.casterName, source: { casterId: a.casterId, spell: a.spell.name, concentration: a.concentration } } as Condition;
}

export const describeAffect = (a: Affect) => a.mode === "damage" ? `${a.amount} ${a.spell.effect && !CONDITION_NAMES.has(a.spell.effect) ? a.spell.effect.toLowerCase() + " " : ""}damage${a.spell.save ? ` · ${a.spell.save}` : ""}` : a.mode === "heal" ? `${a.amount} healing` : a.mode === "condition" ? `${a.conditionName}${a.spell.save ? ` · ${a.spell.save}` : ""}${a.rounds ? ` · ${a.rounds} rounds` : ""}${a.concentration ? " (C)" : ""}` : `${a.spell.name}${a.rounds ? ` · ${a.rounds} rounds` : ""}${a.concentration ? " (C)" : ""}`;

export const emoteLine = (a: Affect, t: AffectTarget, applied: number): string => {
  const who = t.name, by = `${a.casterName}'s ${a.spell.name}`;
  if (a.mode === "damage") return applied > 0 ? `${who} takes ${applied} damage from ${by}${t.share === "half" ? " (saved for half)" : ""}.` : `${who} shrugs off ${by}.`;
  if (a.mode === "heal") return `${by} heals ${who} for ${applied}.`;
  if (a.mode === "condition") return t.failed ? `${who} is ${a.conditionName === a.spell.name ? `affected by ${by}` : `${a.conditionName} by ${by}`}.` : `${who} resists ${by}.`;
  return `${by} is on ${who}.`;
};


/* WHAT THE BUTTON ON THE CARD SHOULD SAY.

   Reported 2026-09-29, after the affect flow first fired: "A 'Affects'
   highlight/link showed up - it was pretty discreet though I don't think a DM
   would know to click it unless told about it."

   Two faults in one sentence. It was drawn as button.link -- no border, no
   background, inline among the dice pills -- and "Affect..." is a word about the
   machinery rather than about the table. A DM mid-fight is not looking for a
   feature, they are looking for the thing that does what they want next, so the
   label names that: heal, hit, help, land. The mode is already decided by
   decideAffect, so this costs nothing to know. */
export const affectVerb = (mode: AffectMode): string =>
  mode === "heal" ? "Choose who to heal"
    : mode === "damage" ? "Choose who is hit"
      : mode === "buff" ? "Choose who it helps"
        /* "condition" covers both a save-or-suffer and a flat imposition, so it
           must not promise a save that some of these spells never roll. */
        : "Choose who it lands on";
